import { clinicAuthClient } from "@auth/clinicAuthClient";
import { useSessionKey, useSessionStore } from "@auth/sessionStore";
import { router } from "@components/AppShell";
import { UserContext, type UserContextValue } from "@context/UserContext";
import { flushPendingRecordings } from "@transcription/recordingsClient";
import { useCurrentUser } from "@users/userQueries";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const userSession = useSessionStore((s) => s.user);
  const isUserAuthed = userSession !== null;

  const sessionKey = useSessionKey();
  const { data: currentUser = null } = useCurrentUser(sessionKey);

  // Flush recordings captured before sign-in.
  useEffect(() => {
    if (!isUserAuthed) return;
    flushPendingRecordings().catch(() => {});
  }, [isUserAuthed]);

  const submitAccessCode = useCallback(async (code: string, pin: string) => {
    const next = await clinicAuthClient.selectUser(code, pin);
    useSessionStore.getState().setUser(next);
  }, []);

  const switchUser = useCallback(() => {
    const user = useSessionStore.getState().user;
    // Before teardown, and replace() so the old page leaves the back-stack on a shared device.
    router.history.replace("/");
    useSessionStore.getState().setUser(null);
    if (user) void clinicAuthClient.logout(user.refreshToken);
  }, []);

  const value = useMemo<UserContextValue>(
    () => ({
      session: userSession,
      currentUser,
      isUserAuthed,
      submitAccessCode,
      switchUser,
    }),
    [userSession, currentUser, isUserAuthed, submitAccessCode, switchUser],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
